import Markdown from '@components/v2/ui/Markdown/Markdown';
import { MARKDOWN_REHYPE_PLUGINS } from '@components/v2/ui/Markdown/markdown.constants';
import type { MarkdownRehypePlugins } from '@components/v2/ui/Markdown/markdown.types';
import type { SxProps, Theme } from '@mui/material';
import { defaultRehypePlugins } from 'streamdown';

type RachelMarkdownVariant = 'body1' | 'body2';
type RachelMarkdownColorToken = 'text.primary' | 'text.secondary';

interface RachelMarkdownProps {
  children: string;
  variant?: RachelMarkdownVariant;
  colorToken?: RachelMarkdownColorToken;
}

const RACHEL_REHYPE_PLUGINS: MarkdownRehypePlugins = [
  defaultRehypePlugins.raw,
  ...MARKDOWN_REHYPE_PLUGINS,
];

const RACHEL_LINE_HEIGHT = 1.5;

const RACHEL_MARKDOWN_STYLES: SxProps<Theme> = (theme) => ({
  lineHeight: RACHEL_LINE_HEIGHT,
  '& p': { lineHeight: RACHEL_LINE_HEIGHT },
  '& li': { lineHeight: RACHEL_LINE_HEIGHT },
  '& h1, & h2, & h3, & h4': { margin: theme.spacing(0.75, 0) },
  '& h1': { fontSize: theme.typography.h4.fontSize },
  '& h2': { fontSize: theme.typography.h5.fontSize },
  '& h3': { fontSize: theme.typography.h6.fontSize },
  '& h4': { fontSize: theme.typography.subtitle2.fontSize },
  '& button': {
    maxWidth: '100%',
    fontFamily: 'inherit',
    lineHeight: RACHEL_LINE_HEIGHT,
    textAlign: 'left',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    cursor: 'pointer',
  },
  '& [data-streamdown="link"]': {
    fontWeight: theme.typography.fontWeightMedium,
    '&:hover': { color: theme.palette.primary.dark },
  },
  '& strong, & [data-streamdown="strong"]': { fontWeight: theme.typography.fontWeightMedium },
  '& code': {
    fontFamily: 'monospace',
    fontSize: theme.typography.body2.fontSize,
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    padding: '1px 5px',
  },
  '& pre': {
    background: theme.palette.action.hover,
    borderRadius: theme.shape.borderRadius,
    padding: '12px',
    overflowX: 'auto',
    margin: '0 0 8px',
  },
  '& pre code': { background: 'none', padding: 0 },
  '& [data-streamdown="code-block-actions"], & [data-streamdown="table-fullscreen"], & [data-streamdown="table-wrapper"] button':
    {
      display: 'none',
    },
  '& [data-streamdown="table-wrapper"]': {
    maxWidth: '100%',
    overflowX: 'auto',
    margin: '0 0 8px',
    background: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '16px',
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    '&:hover': { scrollbarColor: `${theme.palette.grey[300]} transparent` },
    '&::-webkit-scrollbar': { height: 6 },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: 'transparent',
      borderRadius: 999,
    },
    '&:hover::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[300],
    },
  },
  '& table': {
    borderCollapse: 'collapse',
    width: '100%',
    minWidth: 'max-content',
    maxWidth: 'none',
    fontSize: theme.typography.body2.fontSize,
  },
  '& th, & td': {
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderRight: `1px solid ${theme.palette.divider}`,
    padding: '6px 8px',
    textAlign: 'left !important',
    whiteSpace: 'nowrap',
    fontWeight: theme.typography.fontWeightRegular,
  },
  '& th:last-child, & td:last-child': { borderRight: 'none' },
  '& tr:last-child td': { borderBottom: 'none' },
  '& th': {
    background: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightMedium,
  },
  '& tbody tr': { transition: 'background-color 120ms ease' },
  '& tbody tr:hover': { background: theme.palette.background.default },
  '& hr': { borderTop: `1px solid ${theme.palette.grey[300]}` },
  '& img': { maxWidth: '64px' },
});

const RachelMarkdown = ({
  children,
  variant = 'body1',
  colorToken = 'text.primary',
}: RachelMarkdownProps) => (
  <Markdown
    variant={variant}
    colorToken={colorToken}
    rehypePlugins={RACHEL_REHYPE_PLUGINS}
    sx={RACHEL_MARKDOWN_STYLES}
  >
    {children}
  </Markdown>
);

export default RachelMarkdown;
